import React, { useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { FlagOff, Trash2 } from "lucide-react";
import { DataTable, DataTableCard, TableSkeleton } from "../common";
import type { TableColumn } from "../common/Table";
import { ConfirmationModal } from "../common/ConfirmationModal";
import Toggle from "../common/Toggle";
import PageHeader from "../common/PageHeader";
import Button from "../common/Button";
import { IconActionButton } from "../common";
import { SimpleTooltip } from "../common";
import { useHasPermission } from "../../store/hooks/useRbac";
import { PERMISSIONS } from "../../utils/rbac/permissions";
import toast from "react-hot-toast";
import {
  useGetFeatureFlagsQuery,
  useUpdateFeatureFlagMutation,
  useDeleteFeatureFlagMutation,
  type IFeatureFlag,
} from "../../store/apis/featureFlag.api";
import { ApiError } from "../../store/utils/apiError";

/** Figma Feature Flags rows (dc.html:3253) on Table2: name + sub,
 *  state label, switch. Single full-row column, header hidden. */
const FeatureFlagManagement: React.FC = () => {
  const canManage = useHasPermission(PERMISSIONS.FEATURE_FLAGS_MANAGE);
  // Embedded under /admin/settings the hub already renders the header +
  // card container — keep our own chrome only on the standalone route.
  const { pathname } = useLocation();
  const showHeader = !pathname.startsWith("/admin/settings");
  const embedded = !showHeader;

  // UI State
  const [deleteTarget, setDeleteTarget] = useState<IFeatureFlag | null>(null);

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // --- RTK QUERY HOOKS (contracts unchanged) ---
  const {
    data: response,
    isLoading,
    isFetching,
    isError,
    refetch,
  } = useGetFeatureFlagsQuery({
    page: currentPage,
    limit: itemsPerPage,
  });

  const [updateFlag, { isLoading: isUpdating }] = useUpdateFeatureFlagMutation();
  const [deleteFlag, { isLoading: isDeleting }] = useDeleteFeatureFlagMutation();

  const flags = response?.data || [];
  const totalItems = response?.pagination?.total || 0;
  const loading = isLoading || isFetching;

  // --- HANDLERS (logic unchanged, feedback via toast) ---
  const handleToggle = async (flag: IFeatureFlag) => {
    if (!canManage) return;
    const { id, updatedAt, ...editableFields } = flag;
    const result = await updateFlag({
      key: flag.key,
      data: { ...editableFields, enabled: !flag.enabled },
    });

    if ("data" in result) {
      const updated = result.data?.data;
      toast.success(`Feature '${updated?.name || updated?.key}' is now ${updated?.enabled ? "enabled" : "disabled"}.`);
    } else {
      const error = result.error as ApiError;
      const rawMessage = error?.data?.message || error?.response?.data?.message || error?.message || "Failed to toggle feature flag.";
      toast.error(Array.isArray(rawMessage) ? rawMessage.join(", ") : rawMessage);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    const result = await deleteFlag(deleteTarget.key);

    if ("data" in result) {
      toast.success(`Feature '${deleteTarget.name || deleteTarget.key}' deleted.`);
      if (flags.length === 1 && currentPage > 1) setCurrentPage((prev) => prev - 1);
    } else {
      const error = result.error as ApiError;
      const rawMessage = error?.data?.message || error?.response?.data?.message || error?.message || "Failed to delete feature flag.";
      toast.error(Array.isArray(rawMessage) ? rawMessage.join(", ") : rawMessage);
    }
    setDeleteTarget(null);
  };

  const columns = useMemo<TableColumn<IFeatureFlag>[]>(
    () => [
      {
        key: "flag",
        title: "",
        label: "Feature flag",
        required: true,
        // Embedded: hub padding is compensated outside, so cells carry the
        // exact 1rem/14px Figma rhythm. Standalone: native Table2 padding.
        className: embedded ? "px-4 py-[14px]" : undefined,
        render: (_, flag) => (
          <div className="flex items-center gap-3.5 flex-wrap sm:flex-nowrap w-full">
            <div className="flex-1 min-w-[200px]">
              <div className="text-body-md font-bold text-[var(--color-text-primary)] truncate">
                {flag.name || flag.key}
              </div>
              <div className="text-body-xs text-[var(--color-text-tertiary)] mt-0.5 truncate">
                {flag.description || flag.key}
              </div>
            </div>
            <div className="flex items-center gap-3 flex-none ml-auto">
              <span
                className={`text-body-xs font-bold whitespace-nowrap ${
                  flag.enabled
                    ? "text-[var(--color-success-dark)]"
                    : "text-[var(--color-text-tertiary)]"
                }`}
              >
                {flag.enabled ? "Enabled" : "Disabled"}
              </span>
              <Toggle
                checked={flag.enabled}
                onChange={() => handleToggle(flag)}
                disabled={isUpdating || !canManage}
                size="lg"
              />
              {canManage && (
                <SimpleTooltip label="Delete" side="top">
                  <IconActionButton
                    title="Delete feature flag"
                    size={32}
                    variant="danger"
                    onClick={(e) => {
                      e.stopPropagation();
                      e.currentTarget.blur();
                      setDeleteTarget(flag);
                    }}
                  >
                    <Trash2 className="w-4 h-4" aria-hidden="true" />
                  </IconActionButton>
                </SimpleTooltip>
              )}
            </div>
          </div>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [canManage, isUpdating, embedded]
  );

  const table = (
    <DataTable
      columns={columns}
      rows={flags}
      rowKey={(r) => r.key}
      loading={loading}
      skeleton={<TableSkeleton rows={6} columns={1} />}
      emptyMessage="No feature flags found. Flags created for modules will appear here."
      responsive="scroll"
      hideHeader
      pagination={{
        total: totalItems,
        page: currentPage,
        pageSize: itemsPerPage,
        pageSizeOptions: [5, 10, 20, 50],
        onPageChange: setCurrentPage,
        onPageSizeChange: (count) => {
          setItemsPerPage(count);
          setCurrentPage(1);
        },
        autoHide: true,
      }}
    />
  );

  return (
    <div className="w-full">
      {showHeader && (
        <PageHeader
          title="Feature Flags"
          description="Enable or disable modules across the organization"
          breadcrumbs={[
            { label: "Home", path: "/dashboard" },
            { label: "Settings", path: "/admin/settings" },
            { label: "Feature Flags" },
          ]}
        />
      )}

      {isError ? (
        embedded ? (
          <div className="text-center px-6 py-14">
            <h3 className="text-heading-sm text-[var(--color-text-primary)] mb-1">Failed to load feature flags</h3>
            <p className="text-body-sm text-[var(--color-text-secondary)] mb-5">
              Something went wrong while fetching feature flags. Please try again.
            </p>
            <Button appearance="primary" size="small" onClick={() => refetch()}>
              Retry
            </Button>
          </div>
        ) : (
          <div className="bg-[var(--color-bg-base)] border border-[var(--color-border-primary)] rounded-card shadow-[var(--shadow-sm)] text-center px-6 py-14">
            <h3 className="text-heading-sm text-[var(--color-text-primary)] mb-1">Failed to load feature flags</h3>
            <p className="text-body-sm text-[var(--color-text-secondary)] mb-5">
              Something went wrong while fetching feature flags. Please try again.
            </p>
            <Button appearance="primary" size="small" onClick={() => refetch()}>
              Retry
            </Button>
          </div>
        )
      ) : embedded ? (
        <div className="-m-5 sm:-m-8 p-4">{table}</div>
      ) : (
        <DataTableCard title="Feature Flags" count={totalItems}>
          {table}
        </DataTableCard>
      )}

      <ConfirmationModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
        title="Delete Feature Flag"
        message={`Are you sure you want to delete '${deleteTarget?.name || deleteTarget?.key}'? This action cannot be undone.`}
        type="danger"
        confirmText="Delete"
        isLoading={isDeleting}
      />
    </div>
  );
};

export default FeatureFlagManagement;
